/**
 * Discount Configuration
 *
 * Manages first-month discount code mappings for different Shopify plans.
 * Discount codes are configurable via environment variables to allow updates
 * without code deployments.
 *
 * @module discountConfig
 */

/**
 * Plan-to-discount code mapping interface
 */
export interface PlanDiscountMapping {
  [planName: string]: string;
}

/**
 * Discount configuration with validation
 */
class DiscountConfig {
  /**
   * Plan-to-discount code mapping
   * Populated from environment variables with fallback to defaults
   */
  private planDiscountMap: PlanDiscountMapping = {};

  /**
   * Default discount codes (fallback if env vars not set)
   * These are the current production codes as of 2025-10-30
   */
  private readonly DEFAULT_CODES: PlanDiscountMapping = {
    'Basic': '60X1ED',      // 51% off RC Basic
    'Shopify': '02Xxdc',             // 51% off RC Shopify
    'Advanced': 'NXuOAH',    // 60% off RC Advanced
    'Plus': 'xtl3JO',        // 59% off RC Plus
  };

  /**
   * Environment variable prefix for discount codes
   */
  private readonly ENV_PREFIX = 'VUE_APP_DISCOUNT_CODE_';

  constructor() {
    this.loadConfiguration();
  }

  /**
   * Load discount codes from environment variables with fallback to defaults
   *
   * Environment variable format:
   * - VUE_APP_DISCOUNT_CODE_BASIC_SHOPIFY="60X1ED"
   * - VUE_APP_DISCOUNT_CODE_SHOPIFY="02Xxdc"
   * - VUE_APP_DISCOUNT_CODE_ADVANCED_SHOPIFY="NXuOAH"
   * - VUE_APP_DISCOUNT_CODE_SHOPIFY_PLUS="xtl3JO"
   *
   * @private
   */
  private loadConfiguration(): void {
    const envMappings = {
      'Basic': 'BASIC_SHOPIFY',
      'Shopify': 'SHOPIFY',
      'Advanced': 'ADVANCED_SHOPIFY',
      'Plus': 'SHOPIFY_PLUS',
    };

    // Load each plan's discount code from environment or use default
    for (const [planName, envKey] of Object.entries(envMappings)) {
      const envVarName = `${this.ENV_PREFIX}${envKey}`;
      const envValue = process.env[envVarName];

      if (envValue && envValue.trim() !== '') {
        this.planDiscountMap[planName] = envValue.trim();
      } else {
        // Fallback to default
        this.planDiscountMap[planName] = this.DEFAULT_CODES[planName];
      }
    }

    // Validate all discount codes are present
    this.validateConfiguration();
  }

  /**
   * Validate that all required discount codes are configured
   *
   * @throws Error if any discount code is missing or invalid
   * @private
   */
  private validateConfiguration(): void {
    const requiredPlans = ['Basic', 'Shopify', 'Advanced', 'Plus'];
    const missingPlans: string[] = [];

    for (const planName of requiredPlans) {
      const code = this.planDiscountMap[planName];
      if (!code || code.trim() === '') {
        missingPlans.push(planName);
      }
    }

    if (missingPlans.length > 0) {
      const error = `[DiscountConfig] Missing discount codes for plans: ${missingPlans.join(', ')}`;
      throw new Error(error);
    }
  }

  /**
   * Get discount code for a Shopify plan
   *
   * Note: This function expects the short plan key names used internally
   * (e.g., 'Basic', 'Shopify', 'Advanced', 'Plus'), not the full Shopify
   * plan names like 'Basic Shopify' or 'Shopify Plus'.
   *
   * @param shopifyPlanName - Short plan key (e.g., 'Basic', 'Shopify', 'Advanced', 'Plus')
   * @returns Discount code or null if no discount available for plan
   *
   * @example
   * ```typescript
   * const code = discountConfig.getDiscountCode('Basic');
   * // Returns: '60X1ED' (or value from VUE_APP_DISCOUNT_CODE_BASIC_SHOPIFY)
   *
   * const plusCode = discountConfig.getDiscountCode('Plus');
   * // Returns: 'xtl3JO' (or value from VUE_APP_DISCOUNT_CODE_SHOPIFY_PLUS)
   * ```
   */
  public getDiscountCode(shopifyPlanName: string): string | null {
    const code = this.planDiscountMap[shopifyPlanName];

    // Return null for missing entries
    if (code === null || code === undefined) {
      return null;
    }

    // Trim and check for empty/whitespace-only strings (consistent with hasDiscount)
    const trimmedCode = code.trim();
    return trimmedCode === '' ? null : trimmedCode;
  }

  /**
   * Get all plan-to-discount mappings
   *
   * @returns Read-only copy of discount mappings using short plan keys
   *
   * @example
   * ```typescript
   * const mappings = discountConfig.getAllMappings();
   * // Returns: { 'Basic': '60X1ED', 'Shopify': '02Xxdc', 'Advanced': 'NXuOAH', 'Plus': 'xtl3JO' }
   * ```
   */
  public getAllMappings(): Readonly<PlanDiscountMapping> {
    return Object.freeze({ ...this.planDiscountMap });
  }

  /**
   * Check if a plan has a configured discount
   *
   * Note: This function expects the short plan key names (e.g., 'Basic', 'Plus'),
   * not the full Shopify plan names.
   *
   * @param shopifyPlanName - Short plan key (e.g., 'Basic', 'Shopify', 'Advanced', 'Plus')
   * @returns True if discount code exists for plan
   *
   * @example
   * ```typescript
   * if (discountConfig.hasDiscount('Basic')) {
   *   // Apply discount logic
   * }
   *
   * if (discountConfig.hasDiscount('Plus')) {
   *   // Apply Plus plan discount
   * }
   * ```
   */
  public hasDiscount(shopifyPlanName: string): boolean {
    const code = this.planDiscountMap[shopifyPlanName];
    return code !== null && code !== undefined && code.trim() !== '';
  }
}

/**
 * Singleton instance of discount configuration
 * Initialized at module load time
 */
export const discountConfig = new DiscountConfig();

/**
 * Re-export for convenience
 */
export default discountConfig;
